import React, { useCallback, useEffect, useMemo, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Box, Link, Paper, Tab, Tabs, Typography } from "@mui/material";
import { Spinner } from "react-bootstrap";
import authService from "../../../utils/authService";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

/** GET /api/placement-officer/secondary-skills → { skills: [{ id, name, type }], total } */
const SKILL_NAMES_LIST_URL = "placement-officer/secondary-skills";
const SECONDARY_SKILL_NAMES_SESSION_KEY = "lms_ad_secondary_skill_name_map";

const PREVIEW_MAX_W = 320;
/** Fixed video embed size so Old/New table columns match regardless of cell width */
const VIDEO_EMBED_W = PREVIEW_MAX_W;
const VIDEO_EMBED_H = Math.round((VIDEO_EMBED_W * 9) / 16);
const NESTED_SCROLL_MAX_H = 380;

function isPdfUrl(url: string): boolean {
  return /\.pdf(\?|$|#)/i.test(url);
}

function isLikelyImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$|#)/i.test(url);
}

const tabsSx = {
  minHeight: 36,
  mb: 1,
  borderBottom: 1,
  borderColor: "divider",
  "& .MuiTab-root": { minHeight: 36, py: 0.5, fontSize: "0.75rem", textTransform: "none" },
};

function MediaFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        maxWidth: PREVIEW_MAX_W,
        bgcolor: "grey.100",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        p: 1,
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );
}

function FileOrMediaPreview({ url, label }: { url: string; label: string }) {
  if (isLikelyImageUrl(url)) {
    return (
      <MediaFrame>
        <Box
          component="img"
          src={resolveMediaUrl(url)}
          alt={label}
          sx={{ maxWidth: "100%", maxHeight: 220, width: "auto", height: "auto", objectFit: "contain", display: "block" }}
        />
      </MediaFrame>
    );
  }
  if (isPdfUrl(url)) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, maxWidth: PREVIEW_MAX_W, display: "flex", alignItems: "center", gap: 1 }}>
        <PictureAsPdfIcon color="action" fontSize="small" />
        <Link
          href={resolveMediaUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.875rem" }}
        >
          Open PDF
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Link>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5, maxWidth: PREVIEW_MAX_W }}>
      <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.875rem" }}>
        Open file
        <OpenInNewIcon sx={{ fontSize: 16 }} />
      </Link>
    </Paper>
  );
}

let skillNameMapCache: Map<string, string> | null = null;
let skillNameMapInflight: Promise<Map<string, string>> | null = null;

function readSkillMapFromSession(): Map<string, string> | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = sessionStorage.getItem(SECONDARY_SKILL_NAMES_SESSION_KEY);
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return undefined;
    const map = new Map<string, string>();
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "string" && v.trim() !== "") map.set(k, v.trim());
    }
    return map;
  } catch {
    return undefined;
  }
}

function writeSkillMapToSession(map: Map<string, string>): void {
  if (typeof window === "undefined") return;
  try {
    const obj: Record<string, string> = {};
    map.forEach((name, id) => {
      obj[id] = name;
    });
    sessionStorage.setItem(SECONDARY_SKILL_NAMES_SESSION_KEY, JSON.stringify(obj));
  } catch {
    // quota / private mode
  }
}

function parseSkillsListResponse(data: unknown): Map<string, string> {
  const map = new Map<string, string>();
  if (data === null || data === undefined) return map;
  let list: unknown[] = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.skills)) list = o.skills;
    else if (Array.isArray(o.items)) list = o.items;
    else if (Array.isArray(o.results)) list = o.results;
    else if (Array.isArray(o.data)) list = o.data;
  }
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const id = row.id ?? row.skill_id;
    const name = row.name ?? row.skill_name ?? row.title;
    if (id != null && name != null && String(name).trim() !== "") {
      map.set(String(id), String(name).trim());
    }
  }
  return map;
}

/** Loads from memory → sessionStorage → API; used to resolve skill_id → skill_name for secondary_skills */
export function getSecondarySkillNameMap(): Promise<Map<string, string>> {
  if (skillNameMapCache) return Promise.resolve(skillNameMapCache);
  const fromSession = readSkillMapFromSession();
  if (fromSession !== undefined) {
    skillNameMapCache = fromSession;
    return Promise.resolve(skillNameMapCache);
  }
  if (skillNameMapInflight) return skillNameMapInflight;
  skillNameMapInflight = (async () => {
    try {
      const res = await authService.get(SKILL_NAMES_LIST_URL);
      const map = parseSkillsListResponse(res.data);
      skillNameMapCache = map;
      writeSkillMapToSession(map);
      return map;
    } catch (e) {
      console.error("Skill names API failed:", e);
      skillNameMapCache = new Map();
      return skillNameMapCache;
    } finally {
      skillNameMapInflight = null;
    }
  })();
  return skillNameMapInflight;
}

/** Match PO SelectStudents shapes */
interface CertificateItem {
  name?: string;
  cert_url?: string;
}

interface ProjectItem {
  type?: string;
  description?: string;
}

interface VideoItem {
  type?: string;
  video_id?: string;
  status?: string;
  otp?: string;
  playback_info?: string;
}

interface SecondarySkillItem {
  skill_id?: string | number;
  skill_name?: string;
  type?: string;
  level?: string;
  description?: string;
  cert_url?: string;
}

function extractArray(val: unknown): unknown[] {
  if (val === null || val === undefined) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "object") {
    const o = val as Record<string, unknown>;
    const keys = [
      "items",
      "student_primary_certificate_details",
      "student_secondary_certificate_details",
      "student_project_details",
      "videos",
      "secondary_skills",
      "education",
      "education_details",
      "data",
    ];
    for (const k of keys) {
      if (Array.isArray(o[k])) return o[k] as unknown[];
    }
  }
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return extractArray(p);
    } catch {
      return [];
    }
  }
  return [];
}

function getProjectTabLabels(projects: ProjectItem[]) {
  const typeCounts: Record<string, number> = {};
  projects.forEach((p) => {
    const t = p.type || "Project";
    typeCounts[t] = (typeCounts[t] ?? 0) + 1;
  });
  const typeIndex: Record<string, number> = {};
  return projects.map((p, i) => {
    const type = p.type || "Project";
    const total = typeCounts[type] ?? 1;
    const idx = (typeIndex[type] = (typeIndex[type] ?? 0) + 1);
    return { key: String(i), label: total > 1 ? `${type}${idx}` : type };
  });
}

function getVideoTabLabels(videos: VideoItem[]) {
  const typeCounts: Record<string, number> = {};
  videos.forEach((v) => {
    const t = v.type || "Video";
    typeCounts[t] = (typeCounts[t] ?? 0) + 1;
  });
  const typeIndex: Record<string, number> = {};
  return videos.map((v, i) => {
    const type = v.type || "Video";
    const total = typeCounts[type] ?? 1;
    const idx = (typeIndex[type] = (typeIndex[type] ?? 0) + 1);
    return { key: String(i), label: total > 1 ? `${type}${idx}` : type };
  });
}

/** Tab label + preview: certificate name from API */
function certificateTabLabel(c: CertificateItem, index: number): string {
  const n = c?.name?.trim();
  if (n) return n.length > 28 ? `${n.slice(0, 26)}…` : n;
  return `Certificate ${index + 1}`;
}

function CertificatePanel({ value }: { value: unknown }) {
  const list = useMemo(() => extractArray(value) as CertificateItem[], [value]);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setTab(0);
  }, [value]);

  if (list.length === 0) {
    return <Typography variant="caption" color="text.secondary">No data</Typography>;
  }

  const current = list[tab] ?? list[0];
  const single = list.length === 1;
  const label = current?.name?.trim() || "Certificate";

  return (
    <Box sx={{ minWidth: 160, maxWidth: "100%", maxHeight: NESTED_SCROLL_MAX_H, overflow: "auto" }}>
      {!single && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={tabsSx}>
          {list.map((c, i) => (
            <Tab key={i} label={certificateTabLabel(c, i)} value={i} />
          ))}
        </Tabs>
      )}
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.paper" }}>
        {current?.name && (
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {current.name}
          </Typography>
        )}
        {current?.cert_url ? (
          <FileOrMediaPreview url={current.cert_url} label={label} />
        ) : (
          <Typography variant="caption" color="text.secondary">No file URL</Typography>
        )}
      </Paper>
    </Box>
  );
}

function ProjectPanel({ value }: { value: unknown }) {
  const projects = useMemo(() => extractArray(value) as ProjectItem[], [value]);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setTab(0);
  }, [value]);

  if (projects.length === 0) {
    return <Typography variant="caption" color="text.secondary">No projects</Typography>;
  }

  const tabLabels = getProjectTabLabels(projects);
  const current = projects[tab];
  const multiProject = projects.length > 1;

  return (
    <Box sx={{ minWidth: 160, maxWidth: "100%", maxHeight: NESTED_SCROLL_MAX_H, overflow: "auto" }}>
      {multiProject && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={tabsSx}>
          {tabLabels.map((t, i) => (
            <Tab key={t.key} label={t.label} value={i} />
          ))}
        </Tabs>
      )}
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.paper" }}>
        {current && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Type:</strong> {current.type || "—"}
            </Typography>
            <Typography component="div" variant="body2">
              <strong>Description:</strong>
              {current.description ? (
                <Box
                  className="mt-1"
                  sx={{ "& img": { maxWidth: "100%", height: "auto" } }}
                  dangerouslySetInnerHTML={{ __html: current.description }}
                />
              ) : (
                " —"
              )}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
}

function VideoPanel({ value }: { value: unknown }) {
  const videos = useMemo(() => extractArray(value) as VideoItem[], [value]);
  const [tab, setTab] = useState(0);
  const [playback, setPlayback] = useState<{ otp: string; playback_info: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const current = videos[tab];

  const loadPlayback = useCallback(async (v: VideoItem) => {
    setErr(null);
    if (v?.otp && v?.playback_info) {
      setPlayback({ otp: v.otp, playback_info: v.playback_info });
      return;
    }
    if (!v?.video_id) {
      setPlayback(null);
      setErr("Video not available");
      return;
    }
    setLoading(true);
    setPlayback(null);
    try {
      const res = await authService.get(`student/videos/${v.video_id}/`);
      const otp = res.data?.otp ?? "";
      const playbackInfo = res.data?.playback_info ?? "";
      if (otp && playbackInfo) setPlayback({ otp, playback_info: playbackInfo });
      else setErr("Video not available");
    } catch {
      setErr("Failed to load video");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setTab(0);
  }, [value]);

  useEffect(() => {
    if (videos.length === 0) {
      setPlayback(null);
      setErr(null);
      return;
    }
    const v = videos[tab];
    if (v) loadPlayback(v);
  }, [videos, tab, loadPlayback]);

  if (videos.length === 0) {
    return <Typography variant="caption" color="text.secondary">No videos</Typography>;
  }

  const videoTabs = getVideoTabLabels(videos);
  const videoUrl = playback
    ? `https://player.vdocipher.com/v2/?otp=${playback.otp}&playbackInfo=${playback.playback_info}`
    : null;

  return (
    <Box sx={{ minWidth: 160, maxWidth: "100%", maxHeight: NESTED_SCROLL_MAX_H, overflow: "auto" }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={tabsSx}>
        {videoTabs.map((t, i) => (
          <Tab key={t.key} label={t.label} value={i} />
        ))}
      </Tabs>
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.paper", overflow: "hidden" }}>
        {loading ? (
          <Box
            sx={{
              width: VIDEO_EMBED_W,
              height: VIDEO_EMBED_H,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
              borderRadius: 1,
            }}
          >
            <Spinner animation="border" size="sm" />
          </Box>
        ) : err ? (
          <Box sx={{ width: VIDEO_EMBED_W, minHeight: VIDEO_EMBED_H, display: "flex", alignItems: "center" }}>
            <Typography color="error" variant="body2">
              {err}
            </Typography>
          </Box>
        ) : videoUrl && current ? (
          <Box
            sx={{
              position: "relative",
              width: VIDEO_EMBED_W,
              height: VIDEO_EMBED_H,
              flexShrink: 0,
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "grey.900",
            }}
          >
            <iframe
              src={videoUrl}
              title="Video"
              allowFullScreen
              allow="encrypted-media; autoplay; clipboard-write"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}

function skillDisplayName(s: SecondarySkillItem, index: number, nameMap: Map<string, string>): string {
  if (s.skill_name && String(s.skill_name).trim()) return String(s.skill_name).trim();
  const id = s.skill_id != null && s.skill_id !== "" ? String(s.skill_id) : null;
  if (id && nameMap.has(id)) return nameMap.get(id)!;
  const type = (s.type || "Skill").trim();
  if (id) return `${type} · ${id}`;
  return `Skill ${index + 1}`;
}

function SecondarySkillsPanel({ value }: { value: unknown }) {
  const skills = useMemo(() => extractArray(value) as SecondarySkillItem[], [value]);
  const [tab, setTab] = useState(0);
  const [nameMap, setNameMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    setTab(0);
  }, [value]);

  useEffect(() => {
    let cancelled = false;
    getSecondarySkillNameMap().then((m) => {
      if (!cancelled) setNameMap(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (skills.length === 0) {
    return <Typography variant="caption" color="text.secondary">No skills</Typography>;
  }

  const current = skills[tab] ?? skills[0];
  const single = skills.length === 1;
  const title = skillDisplayName(current, tab, nameMap);

  return (
    <Box sx={{ minWidth: 160, maxWidth: "100%", maxHeight: NESTED_SCROLL_MAX_H, overflow: "auto" }}>
      {!single && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={tabsSx}>
          {skills.map((s, i) => (
            <Tab key={`${s.skill_id ?? "s"}-${i}`} label={skillDisplayName(s, i, nameMap)} value={i} />
          ))}
        </Tabs>
      )}
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.paper" }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Type:</strong> {current?.type ?? "—"}
        </Typography>
        <Typography variant="body2" sx={{ mb: current?.description ? 1 : 0 }}>
          <strong>Level:</strong> {current?.level ?? "—"}
        </Typography>
        {current?.description != null && current.description !== "" && (
          <Typography variant="body2" sx={{ mb: current?.cert_url ? 1 : 0 }}>
            <strong>Description:</strong> {current.description}
          </Typography>
        )}
        {current?.cert_url && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              Attachment
            </Typography>
            <FileOrMediaPreview url={current.cert_url} label={title} />
          </Box>
        )}
      </Paper>
    </Box>
  );
}

/** All education rows in one HTML table (Bootstrap table classes) */
function EducationTablePanel({ value }: { value: unknown }) {
  const rows = useMemo(() => extractArray(value) as Record<string, unknown>[], [value]);

  const columnKeys = useMemo(() => {
    const order = ["degree", "institute", "branch", "yop", "cgpa"];
    const seen = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => seen.add(k)));
    const rest = Array.from(seen).filter((k) => !order.includes(k)).sort();
    return [...order.filter((k) => seen.has(k)), ...rest];
  }, [rows]);

  if (rows.length === 0) {
    return <Typography variant="caption" color="text.secondary">No education entries</Typography>;
  }

  return (
    <Box sx={{ minWidth: 200, maxWidth: "100%", maxHeight: NESTED_SCROLL_MAX_H, overflow: "auto" }}>
      <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
        <thead className="table-light">
          <tr>
            {columnKeys.map((k) => (
              <th key={k} className="text-capitalize" style={{ whiteSpace: "nowrap" }}>
                {k.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columnKeys.map((k) => (
                <td key={k} style={{ verticalAlign: "middle" }}>
                  {row[k] === null || row[k] === undefined ? "—" : String(row[k])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function CommunicationPanel({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  if (typeof value === "string") {
    const looksHtml = /<[^>]+>/.test(value);
    return (
      <Paper variant="outlined" sx={{ minWidth: 160, maxWidth: "100%", maxHeight: 280, overflow: "auto", p: 1.5, fontSize: 12 }}>
        {looksHtml ? <div dangerouslySetInnerHTML={{ __html: value }} /> : <span>{value}</span>}
      </Paper>
    );
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const str = o.body ?? o.text ?? o.content ?? o.message;
    if (typeof str === "string") {
      return <CommunicationPanel value={str} />;
    }
    return (
      <Paper variant="outlined" sx={{ fontSize: 11, maxWidth: "100%", maxHeight: 280, overflow: "auto", p: 1.5 }}>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(value, null, 2)}</pre>
      </Paper>
    );
  }
  return <span style={{ fontSize: 12 }}>{String(value)}</span>;
}

export function isNestedProfileFieldKey(fieldKey: string): boolean {
  const k = fieldKey.toLowerCase();
  return (
    k === "primary_certificate" ||
    k === "secondary_certificate" ||
    k === "student_primary_certificate_details" ||
    k === "student_secondary_certificate_details" ||
    k === "secondary_skills" ||
    k === "communication" ||
    k === "video" ||
    k === "videos" ||
    k === "education" ||
    k === "education_details" ||
    k === "project" ||
    k === "projects" ||
    k === "student_project_details"
  );
}

export function NestedProfileFieldValueCell({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  const k = fieldKey.toLowerCase();

  if (
    k === "primary_certificate" ||
    k === "secondary_certificate" ||
    k === "student_primary_certificate_details" ||
    k === "student_secondary_certificate_details"
  ) {
    return <CertificatePanel value={value} />;
  }
  if (k === "project" || k === "projects" || k === "student_project_details") {
    return <ProjectPanel value={value} />;
  }
  if (k === "video" || k === "videos") {
    return <VideoPanel value={value} />;
  }
  if (k === "secondary_skills") {
    return <SecondarySkillsPanel value={value} />;
  }
  if (k === "education" || k === "education_details") {
    return <EducationTablePanel value={value} />;
  }
  if (k === "communication") {
    return <CommunicationPanel value={value} />;
  }

  return (
    <Typography variant="caption" component="span">
      {typeof value === "object" ? JSON.stringify(value) : String(value ?? "—")}
    </Typography>
  );
}
